import type { HttpError } from '@refinedev/core'
import { useDataProvider, useDelete, useNotification } from '@refinedev/core'
import type { TFunction } from 'i18next'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

export function locationDeleteBlockedMessage(t: TFunction, count: number) {
  if (count <= 0) {
    return t('locationsPage.delete.blockedNoCount')
  }
  return t('locationsPage.delete.blocked', { count })
}

/**
 * Deletes a location. The API refuses (CONFLICT) while cabinets are assigned;
 * then the toast says how many have to move first.
 */
export function useDeleteLocation() {
  const { t } = useTranslation()
  const { mutate } = useDelete()
  const { open } = useNotification()
  const dataProvider = useDataProvider()

  const cabinetCount = useCallback(
    async (locationId: string) => {
      try {
        const { total } = await dataProvider().getList({
          resource: 'cabinets',
          pagination: { currentPage: 1, pageSize: 1 },
          filters: [{ field: 'locationId', operator: 'eq', value: locationId }],
        })
        return total
      } catch {
        // the toast falls back to a message without the count
        return 0
      }
    },
    [dataProvider],
  )

  return useCallback(
    (id: string, onSuccess?: () => void) =>
      mutate(
        { resource: 'locations', id, errorNotification: false },
        {
          onSuccess,
          onError: async (error: HttpError) => {
            if (error.statusCode !== 409) {
              open?.({
                type: 'error',
                message: t('locationsPage.delete.failedTitle'),
                description: error.message,
              })
              return
            }
            open?.({
              type: 'error',
              message: t('locationsPage.delete.blockedTitle'),
              description: locationDeleteBlockedMessage(
                t,
                await cabinetCount(id),
              ),
            })
          },
        },
      ),
    [mutate, open, cabinetCount, t],
  )
}
